import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Breadcrumbs,
  Link,
  LinkProps,
  Box,
} from "@mui/material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Navigation } from "types/navigation";
import { motion } from "framer-motion";
import useBreadcrumbs from "use-react-router-breadcrumbs";
import {
  ProjectBreadcrumb,
  BugReportBreadcrumb,
  FeatureRequestBreadcrumb,
  ClientBreadcrumb,
} from "./breadcrumbComponents";

const MotionBox = motion(Box);

interface LinkRouterProps extends LinkProps {
  to: string;
  replace?: boolean;
}

function LinkRouter(props: LinkRouterProps) {
  return <Link {...props} component={RouterLink} />;
}

const routes = [

  { path: "/stakeholder", breadcrumb: "Stakeholder" },
  { path: "/freelancer", breadcrumb: "Freelancer" },
  { path: "/superuser", breadcrumb: "Admin" },
  

  { path: "/stakeholder/projects", breadcrumb: "Projects" },
  { path: "/stakeholder/projects/:projectId", breadcrumb: ProjectBreadcrumb },
  

  { path: "/stakeholder/projects/:projectId/bug-reports", breadcrumb: null }, // Skip this segment
  { 
    path: "/stakeholder/projects/:projectId/bug-reports/:bugReportId", 
    breadcrumb: BugReportBreadcrumb 
  },
  

  { path: "/stakeholder/projects/:projectId/feature-requests", breadcrumb: null }, // Skip this segment
  { 
    path: "/stakeholder/projects/:projectId/feature-requests/:featureRequestId", 
    breadcrumb: FeatureRequestBreadcrumb 
  },
  

  { path: "/client/management/:clientId", breadcrumb: ClientBreadcrumb },
];


interface NavigationBreadcrumbsProps {
  navigationItems: Navigation;
  disabled?: boolean;
}

export const CoreBreadcrumbs: React.FC<NavigationBreadcrumbsProps> = ({
  disabled = false,
}) => {
  const location = useLocation();
  const breadcrumbs = useBreadcrumbs(routes, {
    excludePaths: ["/", "/stakeholder", "/freelancer", "/superuser"], // Exclude user type paths from showing
  });


  if (disabled) {
    return null;
  }

  const pathnames = location.pathname.split("/").filter((x) => x);
  const filteredPathnames = pathnames.filter(
    (segment) => !["bug-reports", "feature-requests"].includes(segment)
  );

  const isUserTypePath =
    filteredPathnames.length === 1 &&
    ["stakeholder", "freelancer", "superuser"].includes(
      filteredPathnames[0].toLowerCase()
    );

  const breadcrumbVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
  };

  const renderBreadcrumb = (breadcrumb: React.ReactNode, pathname: string, isLast: boolean) => {
    const content = (
      <LinkRouter
        underline={isLast ? "none" : "hover"}
        color={isLast ? "text.primary" : "inherit"}
        to={pathname}
        sx={{
          fontSize: 14,
          cursor: isLast ? "default" : "pointer",
          "&:hover": {
            textDecoration: isLast ? "none" : "underline",
          },
        }}
      >
        {breadcrumb}
      </LinkRouter>
    );

    if (isLast) {
      return (
        <Box key={pathname}>
          <MotionBox
            initial="initial"
            animate="animate"
            exit="exit"
            variants={breadcrumbVariants}
            transition={{ duration: 0.2 }}
            sx={{ display: "inline-block" }}
          >
            {content}
          </MotionBox>
        </Box>
      );
    }

    return (
      <Box key={pathname} sx={{ display: "inline-block" }}>
        {content}
      </Box>
    );
  };

  const HomeLink = (
    <Box key="home" sx={{ display: "inline-block" }}>
      <LinkRouter
        to={`/${filteredPathnames[0]}`}
        color="inherit"
        sx={{
          fontSize: 14,
          display: "flex",
          alignItems: "center",
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        <HomeRoundedIcon sx={{ mr: 0.5, fontSize: 20 }} />
        Home
      </LinkRouter>
    </Box>
  );

  const commonBreadcrumbsProps = {
    separator: <ChevronRightRoundedIcon sx={{ fontSize: 16 }} />,
    sx: {
      mb: 2,
      "& .MuiBreadcrumbs-ol": {
        gap: 1,
        display: { xs: "none", sm: "flex" },
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "nowrap",
      },
      "& .MuiBreadcrumbs-li": {
        display: "flex",
        alignItems: "center",
        minHeight: 24,
      },
      "& .MuiBreadcrumbs-separator": {
        mx: 1,
      },
    },
  };

  if (isUserTypePath) {
    return <Breadcrumbs {...commonBreadcrumbsProps}>{HomeLink}</Breadcrumbs>;
  }


  const filteredBreadcrumbs = breadcrumbs.filter((crumb) => {
    const segments = crumb.match.pathname.split("/").filter(x => x);
    return segments.length > 1; // Show breadcrumbs for paths deeper than user type
  });

  return (
    <Breadcrumbs {...commonBreadcrumbsProps}>
      {HomeLink}
      {filteredBreadcrumbs.map((crumb, index) => {
        const isLast = index === filteredBreadcrumbs.length - 1;
        return renderBreadcrumb(crumb.breadcrumb, crumb.match.pathname, isLast);
      })}
    </Breadcrumbs>
  );
};
